import {
  createMutationStore,
  createQueryStore,
  useQueryClient,
} from '@ferreri/minato-react-utils';
import { alert } from '@angeplus/prever-component-lib';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { usePlanVersions } from '../admin/plan-versions/plan-version.store';
import { useSlugState } from '../states/state.store';
import { getCurrentPlan } from './PlansList';
import {
  createPlan,
  getBusinessUnits,
  getPlan,
  getPlanVersion,
  getPlans,
  updatePlan,
} from './plan.client';
import { BusinessUnit, Plan, PlanSetup, PlanVersion } from './plan.types';

export const useNeoPlanVersion = (uf?: string) => {
  const { data, ...rest } = usePlanVersions(uf);

  const filteredPlan = useMemo(() => {
    const plans = data?.filter((planVersion) => {
      return planVersion.slug.includes('neo');
    });
    return plans?.[0];
  }, [data]);

  return { data: filteredPlan, ...rest };
};

export const useFilteredPlanVersions = (uf?: string) => {
  const { data, ...rest } = usePlanVersions(uf);

  const filteredPlans = useMemo(() => {
    return data?.filter((planVersion) => {
      return !planVersion.slug.includes('neo');
    });
  }, [data]);

  return { data: filteredPlans, ...rest };
};

export const usePlanVersion = (slug: string) => {
  return createQueryStore({
    queryKey: ['planVersion', slug],
    queryFn: () => getPlanVersion(slug),
    enabled: !!slug,
  });
};

export const useCurrentStatePlanVersions = () => {
  const { slug } = useSlugState();
  const planVersions = useStatePlanVersions(slug);

  return planVersions;
};

export const useStatePlanVersions = (uf: string) => {
  const planVersions = useFilteredPlanVersions(uf);

  const filteredPlanVersions = planVersions?.data?.map((plan) => {
    const planInfo = plan.plan_distribution.plan;
    const planFixtures = getCurrentPlan(planInfo.slug);
    plan.plan_distribution.plan = { ...planFixtures, ...planInfo };
    return plan;
  });

  return { ...planVersions, data: filteredPlanVersions };
};

function formatPlanVersions(plansVersions?: PlanVersion[]) {
  return plansVersions?.reduce((accumulator, planVersion) => {
    const planSlug = planVersion?.plan_distribution?.plan?.slug;
    accumulator[planSlug] = planVersion;

    return accumulator;
  }, {} as Record<string, PlanVersion>);
}

export const useAllStatePlanVersionsMap = () => {
  const {
    data: planVersionsPR,
    isInitialLoading: isLoadingPR,
    isSuccess: isSuccessPR,
    isError: isErrorPR,
  } = useFilteredPlanVersions('pr');
  const {
    data: planVersionsSC,
    isInitialLoading: isLoadingSC,
    isSuccess: isSuccessSC,
    isError: isErrorSC,
  } = useFilteredPlanVersions('sc');
  const {
    data: planVersionsRS,
    isInitialLoading: isLoadingRS,
    isSuccess: isSuccessRS,
    isError: isErrorRS,
  } = useFilteredPlanVersions('rs');

  const isLoading = isLoadingPR || isLoadingSC || isLoadingRS;
  const isSuccess = isSuccessPR && isSuccessSC && isSuccessRS;
  const isError = isErrorPR || isErrorSC || isErrorRS;

  const pr = useMemo(() => {
    return formatPlanVersions(planVersionsPR);
  }, [planVersionsPR]);

  const sc = useMemo(() => {
    return formatPlanVersions(planVersionsSC);
  }, [planVersionsSC]);

  const rs = useMemo(() => {
    return formatPlanVersions(planVersionsRS);
  }, [planVersionsRS]);

  const data = { pr, sc, rs };

  return { isLoading, isSuccess, isError, data };
};

export const useCurrentPlan = (slug: string) => {
  const { data: planVersion, ...rest } = usePlanVersion(slug);
  const planInfo = planVersion?.plan_distribution.plan;
  const planFixtures = getCurrentPlan(planInfo?.slug);

  const mergedInfo = {
    ...planFixtures,
    ...planVersion,
    ...planInfo,
  };

  return { ...rest, data: mergedInfo };
};

export const useCurrentPlanId = () => {
  const router = useRouter();
  const id = router?.query?.id as string;

  return id;
};

export const useCurrentPlanSlug = () => {
  const id = useCurrentPlanId();
  const { data: planVersion, ...rest } = useCurrentPlan(id);
  const slug = planVersion?.plan_distribution?.plan?.slug;

  return { ...rest, data: slug };
};

export const useCurrentDefaultPlanVersion = () => {
  const defaultSlug = 'essencial';
  const { slug } = useSlugState();
  const planVersions = useFilteredPlanVersions(slug as string);

  const defaultPlanVersion = planVersions?.data?.find(
    (plan) => plan?.plan_distribution?.plan?.slug == defaultSlug,
  );
  return defaultPlanVersion;
};

export const useCurrentStateAllPlanVersions = () => {
  const { slug } = useSlugState();
  const { data: planVersions, ...rest } = usePlanVersions(slug);

  const sortedPlanVersion = planVersions?.sort((first, secound) => {
    if (first?.plan_distribution.plan?.slug == 'neo') return -1;
    if (secound?.plan_distribution.plan?.slug !== 'neo') return 1;
    return 0;
  });

  const mappedPlanVersions = sortedPlanVersion?.map((plan) => {
    const planInfo = plan.plan_distribution.plan;
    const planFixtures = getCurrentPlan(planInfo.slug);
    plan.plan_distribution.plan = { ...planFixtures, ...planInfo };
    return plan;
  });

  return { ...rest, data: mappedPlanVersions };
};

export const useCreatePlan = (config = null) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: PlanSetup) => createPlan(data),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries(['plans']);

      router.push(`/admin/plans`);
    },
    onError: () => {
      alert.helper(
        'Erro ao adicionar plano',
        'Os dados do plano estão incorretos. Por favor, preencha os campos novamente.',
      );
    },
    ...config,
  });
};

export const useGetPlan = () => {
  const router = useRouter();
  const id = router?.query?.plan_id as string;

  return createQueryStore({
    queryKey: ['plan', id],
    queryFn: () => getPlan(id),
    enabled: !!id,
  });
};

export const useUpdatePlan = (config = null) => {
  const router = useRouter();
  const id = router?.query?.plan_id as string;
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: PlanSetup) =>
      updatePlan({
        ...data,
        id,
      }),
    retry: 0,
    onSuccess: (variables) => {
      queryClient.invalidateQueries(['plans']);
      queryClient.invalidateQueries(['plan', variables.id]);

      router.push(`/admin/plans`);
    },
    onError: () => {
      alert.helper(
        'Erro ao atualizar plano',
        'Os dados do plano estão incorretos. Por favor, preencha os campos novamente.',
      );
    },
    ...config,
  });
};

export const usePlans = () => {
  return createQueryStore({
    queryKey: ['plans'],
    queryFn: getPlans,
    refetchOnWindowFocus: false,
  });
};

export const usePlansOptions = () => {
  const queryPlans = usePlans();
  const options = useMemo(
    () =>
      queryPlans.data?.map((plan: Plan) => ({
        label: plan.name,
        value: plan.id,
      })),
    [queryPlans.data],
  );

  return { ...queryPlans, options };
};

export const useBusinessUnits = () => {
  return createQueryStore({
    queryKey: ['business_units'],
    queryFn: getBusinessUnits,
    refetchOnWindowFocus: false,
  });
};

export const useBusinessUnitsOptions = () => {
  const queryBusinessUnits = useBusinessUnits();
  const options = useMemo(
    () =>
      queryBusinessUnits.data?.map((businessUnit: BusinessUnit) => ({
        label: businessUnit.slug,
        value: businessUnit.id,
      })),
    [queryBusinessUnits.data],
  );

  return { ...queryBusinessUnits, options };
};

export const usePlanNameRouteParam = (): string => {
  const router = useRouter();

  return router.query?.plan_name as string;
};
